import { supabase } from "@/integrations/supabase/client";

export interface FAQQuestion {
  id: string;
  question: string;
  answer: string;
  keywords: string[];
  category: string | null;
  priority: number;
  usage_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface FAQMatch {
  faq: FAQQuestion;
  similarity_score: number;
}

export interface ChatConversation {
  id: string;
  user_id: string;
  title: string | null;
  created_at: string;
  updated_at: string;
}

export interface ChatMessage {
  id: string;
  conversation_id: string;
  user_id: string;
  role: 'user' | 'assistant';
  content: string;
  credits_used: number;
  faq_matched: boolean;
  faq_id: string | null;
  created_at: string;
}

/**
 * Calculate similarity score between user question and FAQ question
 * Uses simple keyword matching - can be enhanced with embeddings later
 */
export function calculateSimilarity(userQuestion: string, faq: FAQQuestion): number {
  const userLower = userQuestion.toLowerCase();
  const faqLower = faq.question.toLowerCase();

  // Exact match
  if (userLower === faqLower) return 1.0;

  // Contains full question
  if (userLower.includes(faqLower) || faqLower.includes(userLower)) {
    return 0.9;
  }

  // Keyword matching
  const userWords = userLower.split(/\s+/).filter(w => w.length > 2);
  const faqWords = [...faqLower.split(/\s+/), ...faq.keywords.map(k => k.toLowerCase())];

  let matchCount = 0;
  for (const word of userWords) {
    if (faqWords.some(fw => fw.includes(word) || word.includes(fw))) {
      matchCount++;
    }
  }

  const score = userWords.length > 0 ? matchCount / userWords.length : 0;
  return score;
}

/**
 * Search for FAQ that matches user question
 * Returns best match if similarity score > threshold
 */
export async function searchFAQ(
  userQuestion: string,
  threshold: number = 0.6
): Promise<FAQMatch | null> {
  try {
    // Get all active FAQs ordered by priority
    const { data: faqs, error } = await supabase
      .from('faq_questions')
      .select('*')
      .eq('is_active', true)
      .order('priority', { ascending: false });

    if (error) throw error;
    if (!faqs || faqs.length === 0) return null;

    // Calculate similarity scores
    const matches: FAQMatch[] = faqs
      .map(faq => ({
        faq: faq as FAQQuestion,
        similarity_score: calculateSimilarity(userQuestion, faq as FAQQuestion)
      }))
      .filter(match => match.similarity_score >= threshold)
      .sort((a, b) => b.similarity_score - a.similarity_score);

    return matches.length > 0 ? matches[0] : null;
  } catch (error) {
    console.error('Error searching FAQ:', error);
    return null;
  }
}

/**
 * Record that an FAQ was matched and used
 */
export async function recordFAQMatch(
  faqId: string,
  userQuestion: string,
  userId: string,
  similarityScore: number
): Promise<void> {
  try {
    // Insert match record
    await supabase.from('faq_matches').insert({
      faq_id: faqId,
      user_question: userQuestion,
      user_id: userId,
      similarity_score: similarityScore
    });

    // Increment usage count using stored procedure
    await supabase.rpc('increment_faq_usage', { faq_uuid: faqId });
  } catch (error) {
    console.error('Error recording FAQ match:', error);
  }
}

/**
 * Create or get existing conversation for user
 */
export async function getOrCreateConversation(userId: string): Promise<string | null> {
  try {
    // Try to get most recent conversation
    const { data: existing, error: fetchError } = await supabase
      .from('chat_conversations')
      .select('id')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (existing && !fetchError) {
      return existing.id;
    }

    // Create new conversation
    const { data: newConv, error: insertError } = await supabase
      .from('chat_conversations')
      .insert({
        user_id: userId,
        title: 'AI Chat Session'
      })
      .select('id')
      .single();

    if (insertError) throw insertError;
    return newConv?.id || null;
  } catch (error) {
    console.error('Error getting/creating conversation:', error);
    return null;
  }
}

/**
 * Log a chat message to database
 */
export async function logChatMessage(
  conversationId: string,
  userId: string,
  role: 'user' | 'assistant',
  content: string,
  creditsUsed: number = 0,
  faqMatched: boolean = false,
  faqId: string | null = null
): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('chat_messages')
      .insert({
        conversation_id: conversationId,
        user_id: userId,
        role,
        content,
        credits_used: creditsUsed,
        faq_matched: faqMatched,
        faq_id: faqId
      })
      .select('id')
      .single();

    if (error) throw error;
    return data?.id || null;
  } catch (error) {
    console.error('Error logging chat message:', error);
    return null;
  }
}

/**
 * Get chat history for a user
 */
export async function getChatHistory(
  userId: string,
  conversationId?: string,
  limit: number = 50
): Promise<ChatMessage[]> {
  try {
    let query = supabase
      .from('chat_messages')
      .select('*')
      .eq('user_id', userId);

    if (conversationId) {
      query = query.eq('conversation_id', conversationId);
    }

    const { data, error } = await query
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return (data || []) as ChatMessage[];
  } catch (error) {
    console.error('Error getting chat history:', error);
    return [];
  }
}

/**
 * Get all FAQs (admin function)
 */
export async function getAllFAQs(): Promise<FAQQuestion[]> {
  try {
    const { data, error } = await supabase
      .from('faq_questions')
      .select('*')
      .order('priority', { ascending: false });

    if (error) throw error;
    return (data || []) as FAQQuestion[];
  } catch (error) {
    console.error('Error getting all FAQs:', error);
    return [];
  }
}

/**
 * Create new FAQ (admin function)
 */
export async function createFAQ(
  question: string,
  answer: string,
  keywords: string[],
  category: string | null = null,
  priority: number = 0
): Promise<FAQQuestion | null> {
  try {
    const { data: user } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('faq_questions')
      .insert({
        question,
        answer,
        keywords,
        category,
        priority,
        created_by: user.user?.id
      })
      .select()
      .single();

    if (error) throw error;
    return data as FAQQuestion;
  } catch (error) {
    console.error('Error creating FAQ:', error);
    return null;
  }
}

/**
 * Update FAQ (admin function)
 */
export async function updateFAQ(
  id: string,
  updates: Partial<Omit<FAQQuestion, 'id' | 'created_at' | 'updated_at' | 'usage_count'>>
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('faq_questions')
      .update(updates)
      .eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error updating FAQ:', error);
    return false;
  }
}

/**
 * Delete FAQ (admin function)
 */
export async function deleteFAQ(id: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('faq_questions')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting FAQ:', error);
    return false;
  }
}

/**
 * Get chat statistics (admin function)
 */
export async function getChatStatistics(): Promise<{
  totalConversations: number;
  totalMessages: number;
  totalFAQMatches: number;
  creditsUsed: number;
}> {
  try {
    const { count: convCount } = await supabase
      .from('chat_conversations')
      .select('*', { count: 'exact', head: true });

    const { count: msgCount } = await supabase
      .from('chat_messages')
      .select('*', { count: 'exact', head: true });

    const { count: faqCount } = await supabase
      .from('chat_messages')
      .select('*', { count: 'exact', head: true })
      .eq('faq_matched', true);

    // Fetch credits with limit to reduce IO (covers most use cases)
    // For exact totals on large datasets, consider creating an RPC function with SUM()
    const { data: creditsData } = await supabase
      .from('chat_messages')
      .select('credits_used')
      .limit(10000); // Limit to 10k rows for performance

    const totalCredits = creditsData?.reduce((sum, msg) => sum + (msg.credits_used || 0), 0) || 0;

    return {
      totalConversations: convCount || 0,
      totalMessages: msgCount || 0,
      totalFAQMatches: faqCount || 0,
      creditsUsed: totalCredits
    };
  } catch (error) {
    console.error('Error getting chat statistics:', error);
    return {
      totalConversations: 0,
      totalMessages: 0,
      totalFAQMatches: 0,
      creditsUsed: 0
    };
  }
}
